import { AppConfig } from '®shared/config/app-config';

import { PermissionSubdomain } from './permission-subdomain';
import { Permissions } from './permissions';
import { RoleCodeById, RoleId } from './roles';
import { createUserPermissionsSet } from './user-permission-utils';

/**
 * Converts a permission and subdomain into a lowercase string representation.
 *
 * @param {string} permission the permission
 * @param {?string} subdomain the (optional) subdomain
 * @return {string} the permission and subdomain, in a string
 */
function permissionSubdomainString(permission: string, subdomain?: string) {
  return subdomain ? `${permission}@@@${subdomain.toLowerCase()}` : permission;
}

export class UserPermissions {
  private oauthScopes: Set<string>;
  private scopedPermissions: Set<string>;
  public globals: { [permission: string]: boolean };

  constructor(
    private roleCodesByRoleId: RoleCodeById,
    allPermissions: Set<PermissionSubdomain>,
    oauthScopes: string[] = [],
  ) {
    const permissionStrings = Array.from(allPermissions).map(
      ({ permission, subdomain }) =>
        permissionSubdomainString(permission, subdomain),
    );

    this.scopedPermissions = new Set<string>(permissionStrings);

    this.oauthScopes = new Set(oauthScopes);

    // add convenience booleans for each of the listed global permission constants
    const reduceGlobals = (globals, permission) =>
      Object.assign(globals, {
        [permission]: this.hasGlobalPermission(Permissions[permission]),
      });
    this.globals = Object.freeze(
      Object.keys(Permissions).reduce(reduceGlobals, {}),
    );
  }

  /**
   * Does this user have superuser privileges?
   *
   * @return {boolean} `true` if this user has superuser privileges; `false` otherwise.
   */
  hasSuperuserRole(): boolean {
    return this.oauthScopes.has('su');
  }

  /**
   * Does this user have a specified role
   * @param {string} role`true` if this user has this role; `false` otherwise
   * @return {boolean}
   */
  hasRole(role): boolean {
    return this.oauthScopes.has(role);
  }

  /**
   * Predicate to check whether a user has either:
   * - the provided permission, in the global scope
   * - the provided permission, in the provided scope
   * - superuser privileges
   *
   * @param  {string} permission A permission string
   * @param  {?string} subdomain A subdomain in which permission may be granted
   * @return {boolean} `true` if the user has the permission or `su`; `false` otherwise
   */
  hasPermission(permission: string, subdomain?: string): boolean {
    // superusers have all permissions
    if (this.hasSuperuserRole()) {
      return true;
    }

    // user has a global permission
    const globalPermission = permissionSubdomainString(permission);
    if (this.scopedPermissions.has(globalPermission)) {
      return true;
    }

    // user has permission at specified scope
    if (subdomain) {
      const permissionAtSubdomain = permissionSubdomainString(
        permission,
        subdomain,
      );
      return this.scopedPermissions.has(permissionAtSubdomain);
    }

    // user does not have permission
    return false;
  }

  /**
   * Predicate to check whether the user has any of a number of global permissions.
   *
   * @param  {string[]} ...permissions - a list of permissions
   * @return {boolean} `true` if at least one permission is present; `false` otherwise
   */
  hasGlobalPermission(...permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Returns whether the user has any permissions for this tenant
   * @return {boolean}
   */
  hasNoPermissions(): boolean {
    return this.scopedPermissions.size === 0;
  }

  convertRoleIdToRoleCode(id: RoleId): string | null {
    return this.roleCodesByRoleId[id] || null;
  }

  toString(): string {
    const permissions = Array.from(this.scopedPermissions).join(',');
    return `UserPermissions(${permissions})`;
  }
}

/**
 * A default UserPermission instance indicating default (i.e. no) permissions.
 */
export function createDefaultUserPermissions(
  roleCodesByRoleId: RoleCodeById,
): UserPermissions {
  return new UserPermissions(roleCodesByRoleId, new Set());
}

/**
 * Create a UserPermissions instance for a given AppConfig and list of OAuth scopes
 *
 * @param  {AppConfig} appConfig - the application config, containing role-permission mappings
 * @param  {string[]} userOAuthScopes - a list of OAuth scopes for the user
 * @return {UserPermissions} a UserPermissions instance, for checking permissions.
 */
export function createUserPermissions(
  appConfig: AppConfig,
  userOAuthScopes: string[],
): UserPermissions {
  const { shortCode, roleMapping, roleCodesByRoleId } = appConfig.tenant;
  const permissionSet = createUserPermissionsSet(
    shortCode,
    roleMapping,
    userOAuthScopes,
  );
  return new UserPermissions(roleCodesByRoleId, permissionSet, userOAuthScopes);
}
